import { QueryClient } from '@tanstack/react-query';

/**
 * Default options for TanStack Query
 *
 * Configures global behavior for queries and mutations:
 * - Queries are cached for 5 minutes (staleTime)
 * - Cache persists for 10 minutes (gcTime)
 * - Failed queries retry up to 1 time
 * - Queries refetch on window focus and reconnect
 */
const defaultOptions = {
  queries: {
    // Time until query data is considered stale (5 minutes)
    staleTime: 1000 * 60 * 5,
    // Time until inactive queries are garbage collected (10 minutes)
    gcTime: 1000 * 60 * 10,
    // Retry failed queries once
    retry: 1,
    // Retry delay with exponential backoff
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
    // Refetch on window focus
    refetchOnWindowFocus: true,
    // Refetch on network reconnect
    refetchOnReconnect: true,
    // Don't refetch on mount if data is fresh
    refetchOnMount: true,
  },
  mutations: {
    // Retry failed mutations once
    retry: 1,
    // Retry delay for mutations
    retryDelay: 1000,
  },
};

/**
 * TanStack Query Client instance
 *
 * Manages server state caching, synchronization, and updates throughout the application.
 *
 * Features:
 * - Automatic background refetching
 * - Optimistic updates support
 * - Request deduplication
 * - Automatic retries with exponential backoff
 * - Cache management and garbage collection
 *
 * @example
 * ```typescript
 * import { queryClient } from '@/services/queryClient';
 *
 * // Invalidate queries
 * queryClient.invalidateQueries({ queryKey: ['contracts'] });
 *
 * // Set query data manually
 * queryClient.setQueryData(['contract', id], newContractData);
 *
 * // Prefetch data
 * await queryClient.prefetchQuery({
 *   queryKey: ['contracts'],
 *   queryFn: fetchContracts,
 * });
 * ```
 */
export const queryClient = new QueryClient({
  defaultOptions,
});

/**
 * Query key factory for consistent key generation
 *
 * Ensures query keys follow a consistent pattern across the application
 * and provides type-safe key generation.
 */
export const queryKeys = {
  /**
   * Contract query keys
   */
  contracts: {
    all: ['contracts'] as const,
    lists: () => [...queryKeys.contracts.all, 'list'] as const,
    list: (filters?: object) =>
      [...queryKeys.contracts.lists(), filters] as const,
    details: () => [...queryKeys.contracts.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.contracts.details(), id] as const,
  },

  /**
   * Vendor query keys
   */
  vendors: {
    all: ['vendors'] as const,
    lists: () => [...queryKeys.vendors.all, 'list'] as const,
    list: (filters?: object) =>
      [...queryKeys.vendors.lists(), filters] as const,
    details: () => [...queryKeys.vendors.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.vendors.details(), id] as const,
  },

  /**
   * License query keys
   */
  licenses: {
    all: ['licenses'] as const,
    lists: () => [...queryKeys.licenses.all, 'list'] as const,
    list: (filters?: object) =>
      [...queryKeys.licenses.lists(), filters] as const,
    details: () => [...queryKeys.licenses.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.licenses.details(), id] as const,
    // Nested under the detail key so invalidating a license's detail refetches its allocations too
    allocations: (id: string) => [...queryKeys.licenses.detail(id), 'allocations'] as const,
  },

  /**
   * Cost record query keys
   */
  costRecords: {
    all: ['costRecords'] as const,
    byContract: (contractId: string) => [...queryKeys.costRecords.all, 'contract', contractId] as const,
    summary: (contractId: string) => [...queryKeys.costRecords.all, 'summary', contractId] as const,
  },

  /**
   * Contract milestone (ContractDate) query keys
   */
  contractDates: {
    all: ['contractDates'] as const,
    byContract: (contractId: string) => [...queryKeys.contractDates.all, 'contract', contractId] as const,
  },

  /**
   * Notification query keys
   */
  notifications: {
    all: ['notifications'] as const,
    list: (take?: number) => [...queryKeys.notifications.all, 'list', take] as const,
    unreadCount: () => [...queryKeys.notifications.all, 'unread-count'] as const,
  },

  /**
   * User lookup query keys
   */
  users: {
    all: ['users'] as const,
    list: () => [...queryKeys.users.all, 'list'] as const,
  },

  /**
   * Department lookup query keys
   */
  departments: {
    all: ['departments'] as const,
    list: () => [...queryKeys.departments.all, 'list'] as const,
  },

  /**
   * Dashboard query keys
   */
  dashboard: {
    all: ['dashboard'] as const,
    stats: () => [...queryKeys.dashboard.all, 'stats'] as const,
    charts: () => [...queryKeys.dashboard.all, 'charts'] as const,
    summary: () => [...queryKeys.dashboard.all, 'summary'] as const,
    renewals: (days?: number) => [...queryKeys.dashboard.all, 'renewals', days] as const,
    milestones: (days?: number) => [...queryKeys.dashboard.all, 'milestones', days] as const,
    spendTrends: (months?: number) => [...queryKeys.dashboard.all, 'spendTrends', months] as const,
    contractsByStatus: () => [...queryKeys.dashboard.all, 'contractsByStatus'] as const,
    licenseUtilization: () => [...queryKeys.dashboard.all, 'licenseUtilization'] as const,
    spendByVendor: (top?: number) => [...queryKeys.dashboard.all, 'spendByVendor', top] as const,
    spendByDepartment: () => [...queryKeys.dashboard.all, 'spendByDepartment'] as const,
    renewalForecast: (months?: number, groupBy?: string) =>
      [...queryKeys.dashboard.all, 'renewalForecast', months, groupBy] as const,
    /** Under dashboard.all, so contract, cost record and budget writes refresh it */
    budgetReport: (fiscalYear?: number) => [...queryKeys.dashboard.all, 'budgetReport', fiscalYear] as const,
    costPerUser: (top?: number) => [...queryKeys.dashboard.all, 'costPerUser', top] as const,
  },
};
